import { Action, getModule, Module, Mutation, VuexModule } from 'vuex-module-decorators'
import axios from 'axios'
import qs from 'qs'
import { cloneDeep, kebabCase } from 'lodash'
import { AuthUserEmailPayload, AuthUserLoginPayload, AuthUserPasswordPayload } from './types'
import {
  BaseAccess,
  BaseAccessLevel,
  BaseHook,
  BaseManifest,
  BaseNotificationClient,
  BaseRequestConfig,
  BaseRequestOptions,
  BaseToken,
  BaseUserDetails,
  BaseUserModule
} from '@/types'
import { baseVuexStore } from '@/plugins/vuex-store'
import { createHooks } from '@/utils/hook-util'
import { getEnumOrdinal } from '@/utils/enum-util'

enum StorageKey {
  TOKEN = 'auth.token',
  NOTIFICATION_CLIENT = 'auth.notificationClient'
}

interface TokenPayload {
  // eslint-disable-next-line camelcase
  access_token: string
  // eslint-disable-next-line camelcase
  refresh_token: string
  // eslint-disable-next-line camelcase
  user_details: BaseUserDetails
}

interface NotificationClientPayload {
  cloudPlatform?: string
  token?: string
  oldCloudPlatform?: string
  oldToken?: string
}

const AUTH_STORE_DEFAULT_MANIFEST: BaseManifest = {
  moduleName: 'app',
  moduleBarColor: process.env.VUE_APP_BAR_COLOR,
  modulePrimaryColor: process.env.VUE_APP_PRIMARY_COLOR,
  controllers: []
}
const AUTH_STORE_DEFAULT_ACCESS: BaseAccess = {
  moduleName: 'app',
  accessId: '!',
  accessLevel: BaseAccessLevel.GUEST,
  groupName: '',
  groupReferences: []
}
const AUTH_STORE_ACCESS_ID_VARIABLE = '{accessId}'
const AUTH_STORE_ACCESS_ID_VARIABLE_PATTERN = new RegExp(`\\${AUTH_STORE_ACCESS_ID_VARIABLE}`, 'g')
const AUTH_STORE_APP_BASIC_TOKEN = btoa(`${process.env.VUE_APP_NAME}:${process.env.VUE_APP_SECRET}`)

const tokenHooks = createHooks<BaseToken>()
const userModulesHooks = createHooks<BaseUserModule[]>()
const userModuleHooks = createHooks<BaseUserModule>()
const loginHooks = createHooks<undefined>()
const logoutHooks = createHooks<undefined>()

class RequestOptions<T> implements BaseRequestOptions<T> {
  static readonly TOKEN = new RequestOptions<BaseToken>('/oauth/token?manifests=true', {
    authorized: true,
    headers: {
      Authorization: `Basic ${AUTH_STORE_APP_BASIC_TOKEN}`,
      'Content-Type': 'application/x-www-form-urlencoded'
    }
  })

  static readonly USER_RECOVERY_EMAIL = new RequestOptions<AuthUserEmailPayload>('/auth/users/recovery', {
    authorized: true,
    headers: {
      Authorization: `Basic ${AUTH_STORE_APP_BASIC_TOKEN}`
    }
  })

  static readonly USER_RECOVERY_PASSWORD = new RequestOptions<AuthUserPasswordPayload>('/auth/users/recovery', {
    authorized: true,
    headers: {
      Authorization: `Basic ${AUTH_STORE_APP_BASIC_TOKEN}`
    }
  })

  static readonly NOTIFICATION_CLIENT_REGISTRY = new RequestOptions<NotificationClientPayload>('/auth/notification-clients/registry')

  readonly requestPath: string
  readonly requestConfig?: BaseRequestConfig<T>

  private constructor(requestPath: string, requestConfig?: BaseRequestConfig<T>) {
    this.requestPath = requestPath
    this.requestConfig = requestConfig
  }
}

function normalizeToken(token?: BaseToken | null): BaseToken {
  return token ?? {}
}

function normalizeUserDetails(userDetails?: BaseUserDetails | null): BaseUserDetails {
  return userDetails ?? {}
}

function createUserModules(userDetails: BaseUserDetails): BaseUserModule[] {
  const userModules: BaseUserModule[] = []

  let { manifests, accesses } = userDetails

  accesses?.sort((sourceAccess, targetAccess) => {
    let accessOrder = sourceAccess.moduleName.localeCompare(targetAccess.moduleName)
    if (accessOrder === 0) {
      accessOrder = sourceAccess.groupName.localeCompare(targetAccess.groupName)
    }

    return accessOrder
  })

  manifests = ([] as BaseManifest[]).concat(AUTH_STORE_DEFAULT_MANIFEST, manifests ?? [])
  manifests = cloneDeep(manifests)
  manifests.forEach((manifest) => {
    manifest.title = manifest.title ?? `app.module.${manifest.moduleName}`
    manifest.routePath = manifest.routePath ?? `/${AUTH_STORE_ACCESS_ID_VARIABLE}/${kebabCase(manifest.moduleName)}`

    manifest.controllers.forEach((controller) => {
      controller.title = controller.title ?? `${manifest.moduleName}.${controller.controllerName}.pluralTitle`
      controller.routePath = controller.routePath ?? `${manifest.routePath}/${kebabCase(controller.requestName)}`
    })
  })

  accesses = ([] as BaseAccess[]).concat(AUTH_STORE_DEFAULT_ACCESS, accesses ?? [])
  accesses = cloneDeep(accesses)
  accesses.forEach((access) => {
    let manifest = manifests?.find((manifest) => manifest.moduleName === access.moduleName)

    if (manifest) {
      manifest = JSON.parse(JSON.stringify(manifest).replace(AUTH_STORE_ACCESS_ID_VARIABLE_PATTERN, access.accessId))

      const userModule = Object.assign(access, manifest) as BaseUserModule
      userModule.controllers = userModule.controllers.filter((controller) => !!controller.requests.find((request) => !!request.methods.find((method) =>
        getEnumOrdinal(BaseAccessLevel, method.accessLevel ?? null) <= getEnumOrdinal(BaseAccessLevel, userModule.accessLevel)
      )))

      if ((userModule.moduleName === AUTH_STORE_DEFAULT_MANIFEST.moduleName) || userModule.moduleClientUrl || userModule.controllers.length) userModules.push(userModule)
    }
  })

  return userModules
}

function getUserModule(userModules: BaseUserModule[], accessId?: string | null): BaseUserModule {
  return (accessId ? userModules.find((userModule) => userModule.accessId === accessId) : null) ?? userModules[0]
}

@Module({ name: 'auth', namespaced: true, store: baseVuexStore })
export default class AuthStore extends VuexModule {
  token: BaseToken = normalizeToken()
  userDetails: BaseUserDetails = normalizeUserDetails()
  userModules: BaseUserModule[] = createUserModules(this.userDetails)
  userModule: BaseUserModule = getUserModule(this.userModules)
  notificationClient: BaseNotificationClient | null = null

  private accessId: string | null = null

  @Mutation
  private loadAll(): void {
    this.token = normalizeToken(JSON.parse(localStorage.getItem(StorageKey.TOKEN) as string))
    this.userDetails = normalizeUserDetails(this.token.userDetails)
    this.userModules = createUserModules(this.userDetails)
    this.userModule = getUserModule(this.userModules, this.accessId)
    this.notificationClient = JSON.parse(localStorage.getItem(StorageKey.NOTIFICATION_CLIENT) as string)

    tokenHooks.execute(this.token)
    userModulesHooks.execute(this.userModules)
    userModuleHooks.execute(this.userModule)
  }

  @Mutation
  private setToken(token?: BaseToken): void {
    const normalizedToken = normalizeToken(token)

    if (JSON.stringify(normalizedToken) === JSON.stringify(this.token)) return

    if (token) localStorage.setItem(StorageKey.TOKEN, JSON.stringify(token))
    else localStorage.removeItem(StorageKey.TOKEN)

    this.token = normalizedToken
    this.userDetails = normalizeUserDetails(this.token.userDetails)
    this.userModules = createUserModules(this.userDetails)
    this.userModule = getUserModule(this.userModules, this.accessId)

    tokenHooks.execute(this.token)
    userModulesHooks.execute(this.userModules)
    userModuleHooks.execute(this.userModule)
  }

  @Mutation
  private setNotificationClient(notificationClient?: BaseNotificationClient): void {
    const normalizedNotificationClient = notificationClient ?? null

    if (JSON.stringify(normalizedNotificationClient) === JSON.stringify(this.notificationClient)) return

    if (notificationClient) localStorage.setItem(StorageKey.NOTIFICATION_CLIENT, JSON.stringify(notificationClient))
    else localStorage.removeItem(StorageKey.NOTIFICATION_CLIENT)

    this.notificationClient = normalizedNotificationClient
  }

  @Mutation
  setAccessId(accessId?: string): void {
    const normalizedAccessId = accessId ?? null

    if (normalizedAccessId === this.accessId) return

    this.accessId = normalizedAccessId
    this.userModule = getUserModule(this.userModules, this.accessId)

    userModuleHooks.execute(this.userModule)
  }

  @Action({ rawError: true })
  private async autologin(): Promise<void> {
    const { requestPath, requestConfig } = RequestOptions.TOKEN
    const { refreshToken } = this.token
    const userAutologinQuery = qs.stringify({
      refresh_token: refreshToken,
      grant_type: 'refresh_token'
    })

    const { data } = await axios.post<TokenPayload>(requestPath, userAutologinQuery, requestConfig)

    this.setToken({
      accessToken: data.access_token,
      refreshToken: data.refresh_token,
      userDetails: data.user_details
    })

    loginHooks.execute(undefined)
  }

  @Action({ rawError: true })
  async login(userLoginPayload?: AuthUserLoginPayload): Promise<void> {
    if (!userLoginPayload) return

    const { requestPath, requestConfig } = RequestOptions.TOKEN
    const { email, password } = userLoginPayload
    const userLoginQuery = qs.stringify({
      username: email,
      password,
      grant_type: 'password'
    })

    const { data } = await axios.post<TokenPayload>(requestPath, userLoginQuery, requestConfig)

    this.setToken({
      accessToken: data.access_token,
      refreshToken: data.refresh_token,
      userDetails: data.user_details
    })

    loginHooks.execute(undefined)
  }

  @Action({ rawError: true })
  async logout(): Promise<void> {
    try {
      await this.updateNotificationClient()
    }
    catch (error: any) {
      // eslint-disable-next-line no-console
      console.error('Error during logout:', error)
    }

    this.setToken()

    logoutHooks.execute(undefined)
  }

  @Action({ rawError: true })
  async updateNotificationClient(notificationClient?: BaseNotificationClient): Promise<void> {
    const { requestPath, requestConfig } = RequestOptions.NOTIFICATION_CLIENT_REGISTRY
    const oldNotificationClient = this.notificationClient

    if (notificationClient || oldNotificationClient) {
      const notificationClientPayload: NotificationClientPayload = {
        ...notificationClient
      }
      if (JSON.stringify(notificationClient ?? null) !== JSON.stringify(oldNotificationClient)) {
        notificationClientPayload.oldCloudPlatform = oldNotificationClient?.cloudPlatform
        notificationClientPayload.oldToken = oldNotificationClient?.token
      }

      await axios.post(requestPath, notificationClientPayload, requestConfig)
    }

    this.setNotificationClient(notificationClient)
  }

  @Action({ rawError: true })
  async sendUserRecovery(userEmailPayload?: AuthUserEmailPayload): Promise<void> {
    if (!userEmailPayload) return

    const { requestPath, requestConfig } = RequestOptions.USER_RECOVERY_EMAIL

    return axios.post(requestPath, userEmailPayload, requestConfig)
  }

  @Action({ rawError: true })
  async saveUserRecovery(userPasswordPayload?: AuthUserPasswordPayload): Promise<void> {
    if (!userPasswordPayload) return

    const { requestPath, requestConfig } = RequestOptions.USER_RECOVERY_PASSWORD

    return axios.put(requestPath, userPasswordPayload, requestConfig)
  }

  @Action({ rawError: true })
  async registerTokenHook(tokenHook?: BaseHook<BaseToken>): Promise<void> {
    if (tokenHook) tokenHooks.register(tokenHook)
  }

  @Action({ rawError: true })
  async registerUserModulesHook(userModulesHook?: BaseHook<BaseUserModule[]>): Promise<void> {
    if (userModulesHook) userModulesHooks.register(userModulesHook)
  }

  @Action({ rawError: true })
  async registerUserModuleHook(userModuleHook?: BaseHook<BaseUserModule>): Promise<void> {
    if (userModuleHook) userModuleHooks.register(userModuleHook)
  }

  @Action({ rawError: true })
  async registerLoginHook(loginHook?: BaseHook<undefined>): Promise<void> {
    if (loginHook) loginHooks.register(loginHook)
  }

  @Action({ rawError: true })
  async registerLogoutHook(logoutHook?: BaseHook<undefined>): Promise<void> {
    if (logoutHook) logoutHooks.register(logoutHook)
  }

  @Action({ rawError: true })
  async start(): Promise<void> {
    this.loadAll()

    const { refreshToken } = this.token

    if (refreshToken) {
      try {
        await this.autologin()
      }
      catch (error: any) {
        // eslint-disable-next-line no-console
        console.error('Error during autologin:', error)
      }
    }
    else {
      try {
        await this.logout()
      }
      catch (error: any) {
        // eslint-disable-next-line no-console
        console.error('Error during logout:', error)
      }
    }
  }
}

export const authStore = getModule(AuthStore)